import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Inbox, UserPlus, X, ArrowRightLeft, Check, AlertTriangle, Clock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmployeePicker from '../../components/EmployeePicker';
import { apiGet, apiPut } from '../../lib/api';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending Triage', icon: Inbox },
  { id: 'assigned', label: 'In Progress', icon: Clock },
  { id: 'done', label: 'Done', icon: Check },
  { id: 'dismissed', label: 'Dismissed', icon: AlertTriangle },
];

const DEPARTMENTS = ['projects', 'engineering', 'procurement', 'finance', 'quality', 'hse', 'subcontractors', 'hr'];

export default function DependencyInbox() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // {type:'assign'|'dismiss'|'reroute', taskId, depId}

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (department) params.set('department', department);
      const r = await apiGet(`/tasks/dependencies/inbox?${params.toString()}`);
      setItems(r.items || []);
    } catch { /* leave silent — empty list */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, department]);

  return (
    <div>
      <PageHeader
        overline="Workflow"
        title="Dependency Inbox"
        subtitle="Cross-department task requests routed to your department. Triage them — assign, dismiss with a remark, or reroute."
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setStatus(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border-b-2 transition-colors ${
              status === t.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`dep-tab-${t.id}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Override dept</label>
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white"
            data-testid="dep-inbox-dept-filter"
          >
            <option value="">My department</option>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-sm text-slate-500">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-sm text-slate-500">
          No dependencies in <b className="font-display">{status}</b> for this department.
        </div>
      )}
      <div className="space-y-3">
        {items.map((it) => (
          <DependencyCard
            key={it.dependency.id}
            item={it}
            currentStatus={status}
            onAction={(type) => setAction({ type, taskId: it.task_id, depId: it.dependency.id })}
          />
        ))}
      </div>

      {action && (
        <ActionModal
          action={action}
          onClose={() => setAction(null)}
          onDone={() => { setAction(null); load(); }}
        />
      )}
    </div>
  );
}

function DependencyCard({ item, currentStatus, onAction }) {
  const dep = item.dependency;
  const created = dep.created_by?.name || '—';
  const priorityColor = {
    Critical: 'bg-red-100 text-red-700',
    High: 'bg-indigo-100 text-indigo-700',
    Medium: 'bg-amber-100 text-amber-700',
    Low: 'bg-slate-100 text-slate-600',
  }[dep.priority || 'Medium'];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 hover:border-[#3b4872]/40 transition-colors" data-testid={`dep-card-${dep.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display font-bold mb-1">
            Task · {item.task_title || item.task_id?.slice(0, 8)}
          </div>
          <div className="font-semibold text-slate-900 text-sm leading-snug">{dep.description}</div>
        </div>
        <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${priorityColor}`}>{dep.priority || 'Medium'}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-slate-600 mb-3">
        <Meta label="Requested By" value={created} />
        <Meta label="From Dept" value={dep.created_by?.department || '—'} />
        <Meta label="Due" value={dep.due_date || '—'} />
        <Meta label="Assigned To" value={dep.assigned_to || '—'} />
      </div>
      {currentStatus === 'pending' && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => onAction('assign')} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2f3a5d]" data-testid={`dep-assign-${dep.id}`}>
            <UserPlus size={12} /> Assign
          </button>
          <button onClick={() => onAction('reroute')} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:border-[#3b4872]" data-testid={`dep-reroute-${dep.id}`}>
            <ArrowRightLeft size={12} /> Reroute
          </button>
          <button onClick={() => onAction('dismiss')} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-white border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50" data-testid={`dep-dismiss-${dep.id}`}>
            <X size={12} /> Dismiss
          </button>
        </div>
      )}
      {currentStatus === 'assigned' && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => onAction('done')} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700" data-testid={`dep-done-${dep.id}`}>
            <Check size={12} /> Mark Done
          </button>
        </div>
      )}
      {dep.dismissed_reason && (
        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border-l-2 border-rose-400 px-2 py-1.5">
          <b>Dismissed: </b>{dep.dismissed_reason}
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

function ActionModal({ action, onClose, onDone }) {
  const [assignedTo, setAssignedTo] = useState('');
  const [remark, setRemark] = useState('');
  const [newDept, setNewDept] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (action.type === 'assign') {
        if (!assignedTo) { toast.error('Pick a team member'); setBusy(false); return; }
        await apiPut(`/tasks/${action.taskId}/dependencies/${action.depId}/assign`, { assigned_to: assignedTo });
        toast.success('Dependency assigned');
      } else if (action.type === 'dismiss') {
        if (!remark.trim()) { toast.error('Remark is required'); setBusy(false); return; }
        await apiPut(`/tasks/${action.taskId}/dependencies/${action.depId}/dismiss`, { remark });
        toast.success('Dependency dismissed');
      } else if (action.type === 'reroute') {
        if (!newDept) { toast.error('Pick a department'); setBusy(false); return; }
        await apiPut(`/tasks/${action.taskId}/dependencies/${action.depId}/reroute`, { to_department: newDept, reason: remark });
        toast.success(`Rerouted to ${newDept}`);
      } else if (action.type === 'done') {
        await apiPut(`/tasks/${action.taskId}/dependencies/${action.depId}/status`, { status: 'done' });
        toast.success('Marked done');
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const title = {
    assign: 'Assign to team member',
    dismiss: 'Dismiss this dependency',
    reroute: 'Reroute to another department',
    done: 'Mark dependency as done',
  }[action.type];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid={`dep-action-modal-${action.type}`}>
        <h3 className="font-display font-bold text-base mb-4">{title}</h3>
        {action.type === 'assign' && (
          <div>
            <label className="label">Assigned To *</label>
            <EmployeePicker value={assignedTo} onChange={setAssignedTo} testid="dep-assign-picker" />
          </div>
        )}
        {action.type === 'dismiss' && (
          <div>
            <label className="label">Reason *</label>
            <textarea className="input" rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Explain why this dependency is being dismissed" />
          </div>
        )}
        {action.type === 'reroute' && (
          <div className="space-y-3">
            <div>
              <label className="label">Re-route to Department *</label>
              <select className="input" value={newDept} onChange={(e) => setNewDept(e.target.value)}>
                <option value="">Select department…</option>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Reason</label>
              <textarea className="input" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} />
            </div>
          </div>
        )}
        {action.type === 'done' && <p className="text-sm text-slate-600">This will mark the dependency as Done and move it out of the active queue.</p>}
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="dep-action-submit">{busy ? 'Saving…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}
